import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import { fetchPayrollFieldsThunk, addPayrollFieldValueThunk } from "./payrollFieldThunks";

interface FieldValue {
  field_code: string;
  field_value: string;
  effective_date: string;
}

interface PayrollState {
  fields: any[];
  fieldValues: Record<string, FieldValue>;
  loading: boolean;
  savingField: string | null;
  error?: string | null;
}

const initialState: PayrollState = {
  fields: [],
  fieldValues: {},
  loading: false,
  savingField: null,
  error: null,
};

const payrollSlice = createSlice({
  name: "payrollField",
  initialState,
  reducers: {
    setFieldValue(state, action: PayloadAction<{ fieldCode: string; value: string }>) {
      const { fieldCode, value } = action.payload;
      state.fieldValues[fieldCode] = {
        ...(state.fieldValues[fieldCode] || { field_code: fieldCode, field_value: "", effective_date: "" }),
        field_value: value,
      };
    },
    setFieldDate(state, action: PayloadAction<{ fieldCode: string; date: string }>) {
      const { fieldCode, date } = action.payload;
      state.fieldValues[fieldCode] = {
        ...(state.fieldValues[fieldCode] || { field_code: fieldCode, field_value: "", effective_date: "" }),
        effective_date: date,
      };
    },
    clearError(state) {
      state.error = null;
    }
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchPayrollFieldsThunk.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchPayrollFieldsThunk.fulfilled, (state, action) => {
        state.loading = false;
        const response = action.payload as any;
        state.fields = response.data || [];
        const initialValues: Record<string, FieldValue> = {};
        (response.data || []).forEach((field: any) => {
          initialValues[field.field_code] = {
            field_code: field.field_code,
            field_value: field.field_value || "",
            effective_date: field.effective_date || "",
          };
        });
        state.fieldValues = initialValues;
      })
      .addCase(fetchPayrollFieldsThunk.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string || 'Failed to fetch';
      })
      .addCase(addPayrollFieldValueThunk.pending, (state, action) => {
        state.savingField = (action.meta.arg as any)?.field_code || null;
      })
      .addCase(addPayrollFieldValueThunk.fulfilled, (state, action) => {
        state.savingField = null;
      })
      .addCase(addPayrollFieldValueThunk.rejected, (state, action) => {
        state.savingField = null;
        state.error = action.payload as string || 'Failed to save';
      });
  },
});

export const { setFieldValue, setFieldDate, clearError } = payrollSlice.actions;
export default payrollSlice.reducer;